import { useMemo, useState } from "react";
import { MdCheck, MdSearch } from "react-icons/md";
import {
  SUBJECT_ICON_OPTIONS,
  getSubjectIconOption,
  type SubjectIconKey,
} from "constants/subjectIcons";
import { UI_INPUT } from "lib/uiClasses";

type SubjectIconPickerProps = {
  value: string | null | undefined;
  onChange: (next: SubjectIconKey) => void;
  isArabic: boolean;
  disabled?: boolean;
};

export default function SubjectIconPicker({
  value,
  onChange,
  isArabic,
  disabled = false,
}: SubjectIconPickerProps) {
  const [query, setQuery] = useState("");
  const selected = getSubjectIconOption(value);
  const dir = isArabic ? "rtl" : "ltr";

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SUBJECT_ICON_OPTIONS;
    return SUBJECT_ICON_OPTIONS.filter((option) => {
      const label = (isArabic ? option.labelAr : option.labelEn).toLowerCase();
      return label.includes(q) || option.value.toLowerCase().includes(q);
    });
  }, [query, isArabic]);

  return (
    <div
      className={`space-y-3 ${disabled ? "pointer-events-none opacity-60" : ""}`}
      dir={dir}
    >
      <div
        className="relative overflow-hidden rounded-2xl border px-3.5 py-3"
        style={{
          borderColor: `${selected.accent}55`,
          background: `linear-gradient(135deg, ${selected.accent}24 0%, rgba(15,18,28,0.92) 55%)`,
        }}
      >
        <div className="relative z-[1] flex items-center gap-3">
          <span
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl shadow-lg"
            style={{
              backgroundColor: `${selected.accent}33`,
              color: selected.accent,
              boxShadow: `0 8px 24px ${selected.accent}33`,
            }}
          >
            <selected.Icon className="h-6 w-6" aria-hidden />
          </span>
          <div className="min-w-0 flex-1 text-start">
            <p className="text-[11px] font-medium text-white/55">
              {isArabic ? "الأيقونة المختارة" : "Selected icon"}
            </p>
            <p className="truncate text-base font-bold text-white">
              {isArabic ? selected.labelAr : selected.labelEn}
            </p>
          </div>
          <span
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
            style={{ backgroundColor: `${selected.accent}33`, color: selected.accent }}
          >
            <MdCheck className="h-4 w-4" aria-hidden />
          </span>
        </div>
      </div>

      <label className={`${UI_INPUT} flex cursor-text items-center gap-2.5 !py-0`}>
        <MdSearch className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
        <input
          type="text"
          role="searchbox"
          inputMode="search"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={isArabic ? "ابحث عن أيقونة مادة..." : "Search subject icons..."}
          className="min-w-0 flex-1 appearance-none border-0 bg-transparent py-3 text-start text-sm text-inherit outline-none placeholder:text-slate-500"
          disabled={disabled}
        />
      </label>

      <div className="max-h-[280px] overflow-y-auto rounded-2xl border border-white/10 bg-[var(--apple-field)] p-2.5">
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {filtered.map((option) => {
            const active = option.value === selected.value;
            const label = isArabic ? option.labelAr : option.labelEn;
            return (
              <button
                key={option.value}
                type="button"
                disabled={disabled}
                onClick={() => onChange(option.value)}
                className={`group relative flex flex-col items-center gap-2 rounded-2xl border px-2 py-3 transition ${
                  active
                    ? "border-transparent shadow-[0_0_0_1px_rgba(255,255,255,0.08)]"
                    : "border-white/[0.06] bg-white/[0.03] hover:border-white/15 hover:bg-white/[0.06]"
                }`}
                style={
                  active
                    ? {
                        backgroundColor: `${option.accent}1F`,
                        boxShadow: `inset 0 0 0 1px ${option.accent}99, 0 8px 20px ${option.accent}22`,
                      }
                    : undefined
                }
                aria-pressed={active}
                title={label}
              >
                {active ? (
                  <span
                    className="absolute end-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full"
                    style={{ backgroundColor: option.accent, color: "#0b1018" }}
                  >
                    <MdCheck className="h-2.5 w-2.5" aria-hidden />
                  </span>
                ) : null}
                <span
                  className="flex h-11 w-11 items-center justify-center rounded-2xl transition group-hover:scale-[1.04]"
                  style={{
                    backgroundColor: `${option.accent}${active ? "40" : "22"}`,
                    color: option.accent,
                  }}
                >
                  <option.Icon className="h-5 w-5" aria-hidden />
                </span>
                <span className="line-clamp-2 min-h-[2rem] w-full text-center text-[11px] font-semibold leading-tight text-white/85">
                  {label}
                </span>
              </button>
            );
          })}
        </div>
        {filtered.length === 0 ? (
          <p className="px-2 py-8 text-center text-xs text-white/45">
            {isArabic ? "لا توجد نتائج" : "No results"}
          </p>
        ) : null}
      </div>
    </div>
  );
}
